import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Loader2, PlayCircle, CheckCircle2, Clock, TrendingUp, ArrowRight, Star } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popMyEnrollments, popMyBookings, popUpdateProgress, popRate } from '../../lib/popApi';

const StatusBadge = ({ status }) => {
  const meta = {
    enrolled:    { color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    label: 'Enrolled' },
    in_progress: { color: 'text-amber-300',   bg: 'bg-amber-500/15',   label: 'In progress' },
    completed:   { color: 'text-emerald-300', bg: 'bg-emerald-500/15', label: 'Completed' },
    cancelled:   { color: 'text-white/50',    bg: 'bg-white/10',       label: 'Cancelled' },
  }[status] || { color: 'text-white/60', bg: 'bg-white/10', label: status };
  return <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${meta.bg} ${meta.color}`}>{meta.label}</span>;
};

const ProgressBar = ({ pct }) => (
  <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
    <div className="h-full bg-gradient-to-r from-amber-300 to-emerald-400 transition-all" style={{ width: `${pct}%` }} />
  </div>
);

const StarRow = ({ value, onChange, size = 3.5 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} onClick={() => onChange && onChange(s)}
        className={`transition ${onChange ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}
        aria-label={`${s} star`}
        data-testid={`rate-star-${s}`}>
        <Star className={`w-${size} h-${size} ${s <= value ? 'fill-amber-300 text-amber-300' : 'text-white/25'}`} />
      </button>
    ))}
  </div>
);

const EnrollmentCard = ({ e, onProgress, onRate }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(e.progress_pct || 0);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  const bump = async (val) => {
    setBusy(true);
    try {
      const r = await onProgress(e.id, val);
      setPct(r.enrollment.progress_pct);
      setSavedMsg('Progress saved');
      setTimeout(() => setSavedMsg(''), 1500);
    } catch (_e) { /* noop */ }
    setBusy(false);
  };

  const submitRating = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      await onRate(e.subject_type, e.subject_id, stars, review);
      setSavedMsg('Rating submitted · thank you');
      setRatingOpen(false);
      setTimeout(() => setSavedMsg(''), 2000);
    } catch (_err) { /* noop */ }
    setBusy(false);
  };

  const Icon = e.subject_type === 'event' ? Calendar : BookOpen;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5" data-testid={`enrollment-card-${e.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 font-bold">
          <Icon className="w-3 h-3" /> {e.subject_type === 'event' ? 'Session' : 'Course'}
        </span>
        <StatusBadge status={e.status} />
      </div>
      <div className="text-base font-serif font-semibold text-white mb-3">{e.subject_title || `Item ${e.subject_id.slice(0, 6)}`}</div>
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] text-white/60 mb-1">
          <span>Progress</span><span className="tabular-nums">{pct}%</span>
        </div>
        <ProgressBar pct={pct} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {[25, 50, 75, 100].map((v) => (
          <button key={v} onClick={() => bump(v)} disabled={busy}
            data-testid={`progress-${e.id}-${v}`}
            className={`px-2 py-1 rounded text-[10px] font-semibold border transition ${
              pct >= v ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
            }`}>
            {v}%
          </button>
        ))}
        {e.status === 'completed' && (
          <button onClick={() => setRatingOpen(!ratingOpen)} data-testid={`rate-open-${e.id}`}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-white/5 border border-white/10 text-emerald-200 hover:bg-emerald-500/10">
            <Star className="w-3 h-3" /> Rate
          </button>
        )}
      </div>
      {savedMsg && <div className="text-[10px] text-emerald-300 mt-2">{savedMsg}</div>}
      {ratingOpen && (
        <div className="mt-3 rounded-lg border border-white/10 bg-slate-950 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-2">How would you rate this?</div>
          <StarRow value={stars} onChange={setStars} />
          <textarea value={review} onChange={(ev) => setReview(ev.target.value)} rows={2} placeholder="Optional review…"
            className="mt-2 w-full px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-xs text-white placeholder:text-white/30" />
          <button onClick={submitRating} disabled={!stars || busy}
            data-testid={`rate-submit-${e.id}`}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-xs font-semibold">
            Submit rating
          </button>
        </div>
      )}
    </div>
  );
};

const BookingCard = ({ b, onUpdate, isExpertView }) => {
  const [busy, setBusy] = useState(false);
  const doUpdate = async (status) => {
    setBusy(true);
    try { await onUpdate(b.id, status); } catch (_e) { /* noop */ }
    setBusy(false);
  };
  const meta = {
    requested: { color: 'text-amber-300',   bg: 'bg-amber-500/15',   label: 'Requested' },
    confirmed: { color: 'text-emerald-300', bg: 'bg-emerald-500/15', label: 'Confirmed' },
    declined:  { color: 'text-rose-300',    bg: 'bg-rose-500/15',    label: 'Declined' },
    cancelled: { color: 'text-white/50',    bg: 'bg-white/10',       label: 'Cancelled' },
    completed: { color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    label: 'Completed' },
  }[b.status] || { color: 'text-white/60', bg: 'bg-white/10', label: b.status };
  const when = new Date(b.starts_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4" data-testid={`booking-card-${b.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">1:1 Mentorship</div>
        <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${meta.bg} ${meta.color}`}>{meta.label}</span>
      </div>
      <div className="text-sm font-serif font-semibold text-white leading-tight">
        {isExpertView ? `With ${b.student_name}` : `With ${b.expert_name}`}
      </div>
      <div className="text-[11px] text-white/60 mt-1">{b.topic || 'General mentorship'}</div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-white/50">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {when}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {b.duration_minutes} min</span>
      </div>
      {b.question && <div className="mt-2 rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-[11px] text-white/70 italic">&ldquo;{b.question}&rdquo;</div>}
      {isExpertView && b.status === 'requested' && (
        <div className="flex gap-1.5 mt-3">
          <button onClick={() => doUpdate('confirmed')} disabled={busy} data-testid={`booking-confirm-${b.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold bg-emerald-500 hover:bg-emerald-400 text-white">
            Confirm
          </button>
          <button onClick={() => doUpdate('declined')} disabled={busy} data-testid={`booking-decline-${b.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold bg-white/5 hover:bg-rose-500/10 border border-rose-400/30 text-rose-300">
            Decline
          </button>
        </div>
      )}
      {b.status === 'confirmed' && (
        <button onClick={() => doUpdate('completed')} disabled={busy} data-testid={`booking-complete-${b.id}`}
          className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white/80">
          Mark completed
        </button>
      )}
    </div>
  );
};

const PoPLearning = () => {
  const { user } = usePoPAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [bookings, setBookings] = useState({ as_student: [], as_expert: [] });
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState('all');

  const load = async () => {
    setBusy(true);
    try {
      const [e, b] = await Promise.all([popMyEnrollments(), popMyBookings()]);
      setEnrollments(e.results || []);
      setBookings({ as_student: b.as_student || [], as_expert: b.as_expert || [] });
    } catch (_e) { /* noop */ } finally { setBusy(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const doProgress = async (id, pct) => {
    const r = await popUpdateProgress(id, pct);
    setEnrollments((prev) => prev.map((x) => x.id === id ? r.enrollment : x));
    return r;
  };
  const doRate = async (subject_type, subject_id, stars, review) => await popRate(subject_type, subject_id, stars, review);

  const filtered = tab === 'all' ? enrollments :
                   tab === 'in_progress' ? enrollments.filter((e) => e.status !== 'completed') :
                   enrollments.filter((e) => e.status === 'completed');

  const completedCount = enrollments.filter((e) => e.status === 'completed').length;

  return (
    <PoPShell activeTab="learning">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">MY LEARNING</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Your growth timeline.</h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">Courses you&apos;ve enrolled in, sessions you&apos;ve booked, and progress you&apos;ve made.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {[
          { l: 'Total Enrolled',       v: enrollments.length,                      Icon: BookOpen },
          { l: 'In Progress',          v: enrollments.filter((e) => e.status === 'in_progress').length, Icon: PlayCircle },
          { l: 'Completed',            v: completedCount,                          Icon: CheckCircle2 },
          { l: 'Upcoming Sessions',    v: bookings.as_student.filter((b) => ['requested', 'confirmed'].includes(b.status)).length, Icon: Calendar },
        ].map((s) => (
          <div key={s.l} className="rounded-lg border border-white/10 bg-slate-900/40 p-4">
            <s.Icon className="w-4 h-4 text-amber-300 mb-2" />
            <div className="text-2xl font-serif font-bold text-white tabular-nums leading-none">{s.v}</div>
            <div className="text-[10px] tracking-wider uppercase text-white/50 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Enrollments */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl font-semibold">Enrollments</h2>
          <div className="flex gap-1.5">
            {[
              { id: 'all',         label: 'All' },
              { id: 'in_progress', label: 'Active' },
              { id: 'completed',   label: 'Done' },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`learning-tab-${t.id}`}
                className={`px-3 py-1 rounded text-[11px] font-medium border transition ${
                  tab === t.id ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                }`}>{t.label}</button>
            ))}
          </div>
        </div>
        {busy ? (
          <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
            <TrendingUp className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-sm text-white/60 mb-2">No enrollments yet.</div>
            <Link to="/pop/library" className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 font-semibold">
              Explore the library <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e) => <EnrollmentCard key={e.id} e={e} onProgress={doProgress} onRate={doRate} />)}
          </div>
        )}
      </div>

      {/* Bookings */}
      <div>
        <h2 className="font-serif text-xl font-semibold mb-3">Mentorship sessions</h2>
        {bookings.as_student.length === 0 && bookings.as_expert.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
            <Calendar className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-sm text-white/60 mb-2">No sessions booked yet.</div>
            <Link to="/pop/directory" className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 font-semibold">
              Find a practitioner <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {bookings.as_student.length > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-2">AS STUDENT</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {bookings.as_student.map((b) => <BookingCard key={b.id} b={b} onUpdate={async (id, s) => { await import('../../lib/popApi').then((m) => m.popUpdateBookingStatus(id, s)); load(); }} isExpertView={false} />)}
                </div>
              </div>
            )}
            {bookings.as_expert.length > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-2">AS MENTOR</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {bookings.as_expert.map((b) => <BookingCard key={b.id} b={b} onUpdate={async (id, s) => { await import('../../lib/popApi').then((m) => m.popUpdateBookingStatus(id, s)); load(); }} isExpertView={true} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PoPShell>
  );
};

export default PoPLearning;
